import React, { useEffect, useState } from 'react';
import { useStore } from '../store';
import { Menu, Upload, BarChart2, Settings, PieChart, FileDown, Home, RefreshCw, BookOpen, Users, AlertCircle, BarChart3 } from 'lucide-react';
import { canNavigateToStep } from '../lib/workflowUtils';

export function Sidebar() {
  const { 
    currentPage, 
    setCurrentPage, 
    hasFile, 
    hasAxes, 
    hasScaleMapping, 
    hasAnalysisResults,
    clearAllData 
  } = useStore();

  const [isAdmin, setIsAdmin] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is admin by decoding JWT token
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      try {
        // Decode JWT payload (middle part)
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Check if email is admin (you can add role to JWT later)
        setIsAdmin(payload.email === 'admin@admin.com' || payload.role === 'admin');
      } catch (e) {
        setIsAdmin(false);
      }
    }
  }, []);

  const handleNavigation = (pageId: string) => {
    const { allowed, reason } = canNavigateToStep(pageId);
    
    if (allowed) {
      setCurrentPage(pageId);
      setNavigationError(null);
    } else {
      setNavigationError(reason || 'لا يمكن الانتقال إلى هذه الخطوة');
      setTimeout(() => setNavigationError(null), 3000);
    }
  };

  const navItems = [
    { id: 'file_upload', label: 'رفع الملف', icon: Upload, enabled: true },
    { id: 'axis_management', label: 'إدارة المحاور', icon: Menu, enabled: hasFile },
    { id: 'scale_config', label: 'إعداد المقياس', icon: Settings, enabled: hasFile && hasAxes },
    { id: 'analysis', label: 'تحليل البيانات', icon: BarChart2, enabled: hasFile && hasAxes && hasScaleMapping },
    { id: 'dashboard', label: 'لوحة المعلومات', icon: PieChart, enabled: hasAnalysisResults },
    { id: 'charts', label: '📊 الرسوم البيانية', icon: BarChart3, enabled: hasAnalysisResults },
    { id: 'export', label: 'تصدير النتائج', icon: FileDown, enabled: hasAnalysisResults },
    { id: 'instructions', label: 'دليل الاستخدام', icon: BookOpen, enabled: true },
    ...(isAdmin ? [{ id: 'user_management', label: 'إدارة المستخدمين', icon: Users, enabled: true }] : [])
  ];

  const handleClearData = () => {
    if (window.confirm('هل أنت متأكد من رغبتك في مسح جميع البيانات والبدء من جديد؟')) {
      clearAllData();
      setCurrentPage('file_upload');
    }
  };

  return (
    <div
      style={{
        width: 260,
        height: '100vh',
        position: 'fixed',
        right: 0,
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(13,18,33,0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderLeft: '1px solid rgba(255,255,255,0.07)',
        zIndex: 100,
        padding: '24px 16px',
        overflowY: 'auto',
      }}
    >
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, padding: '0 8px' }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(99,102,241,0.45)',
          fontSize: 20,
        }}>📊</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>محلل الاستبانات</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>جامعة الإسراء</div>
        </div>
      </div>

      {/* Navigation Error */}
      {navigationError && (
        <div style={{
          marginBottom: 12, padding: '10px 12px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <AlertCircle style={{ width: 14, height: 14, color: '#FCA5A5', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: '#FCA5A5', lineHeight: 1.4 }}>{navigationError}</p>
        </div>
      )}

      {/* Section label */}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-muted)', padding: '0 10px', marginBottom: 8 }}>
        خطوات التحليل
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map((item, index) => {
            const isActive = currentPage === item.id;
            const isLocked = !item.enabled;
            const isSpecial = ['instructions','user_management'].includes(item.id);
            return (
              <li key={item.id}>
                <button
                  onClick={() => handleNavigation(item.id)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    borderRadius: 10,
                    border: isActive ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                    background: isActive
                      ? 'linear-gradient(135deg,rgba(99,102,241,0.18),rgba(139,92,246,0.12))'
                      : 'transparent',
                    color: isActive ? '#818CF8'
                      : isLocked ? 'var(--text-muted)'
                      : 'var(--text-secondary)',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    textAlign: 'right',
                    transition: 'all 0.18s ease',
                    boxShadow: isActive ? '0 0 12px rgba(99,102,241,0.15)' : 'none',
                    opacity: isLocked ? 0.45 : 1,
                    marginBottom: isSpecial && index > 0 ? 0 : 0,
                  }}
                  onMouseEnter={e => {
                    if (!isLocked && !isActive) {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isLocked && !isActive) {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  {/* Step number badge for workflow steps */}
                  {!isSpecial ? (
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      background: isActive ? 'linear-gradient(135deg,#6366F1,#8B5CF6)' : 'rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700,
                      color: isActive ? 'white' : 'var(--text-muted)',
                      boxShadow: isActive ? '0 2px 8px rgba(99,102,241,0.4)' : 'none',
                    }}>
                      {index + 1}
                    </div>
                  ) : (
                    <item.icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, flex: 1 }}>
                    {item.label}
                  </span>
                  {isLocked && !isSpecial && (
                    <span style={{
                      fontSize: 9, padding: '2px 6px', borderRadius: 99,
                      background: 'rgba(255,255,255,0.05)',
                      color: 'var(--text-muted)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}>مقفل</span>
                  )}
                  {isActive && (
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#6366F1', flexShrink: 0 }} />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Divider */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '16px 0' }} />

      {/* Clear data button */}
      <button
        onClick={handleClearData}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)',
          background: 'rgba(239,68,68,0.06)', color: '#FCA5A5',
          cursor: 'pointer', textAlign: 'right', transition: 'all 0.18s ease',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.14)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.06)';
        }}
      >
        <RefreshCw style={{ width: 14, height: 14 }} />
        <span style={{ fontSize: 13, fontWeight: 500 }}>بدء تحليل جديد</span>
      </button>
    </div>
  );
}